// src/components/Services.jsx
import { ChevronRight, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { GreenIconTile, TILE_ICON_SIZE } from "./GreenIconTile";
import { ResolveCmsIcon } from "./ResolveCmsIcon";
import ConstructionModal from "./ConstructionModal";
import data from "../data/content.json";
import { useSiteHome } from "../hooks/useSiteHome";

const Services = () => {
  const { data: home } = useSiteHome();
  // Verificación defensiva de datos
  const servicesSection = home?.servicesSection || data?.servicesSection || { items: [] };
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section id="servicios" className="py-32 bg-[#f5f9f6] relative z-[1]">
      {/* Wrapper aislado para el blur decorativo — overflow:hidden aquí no afecta al texto */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/5 blur-[120px] rounded-full" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* HEADER DE SECCIÓN: ESTILO ÉLITE CON TIPOGRAFÍA MONTSERRAT & INTER */}
        <div className="mb-20">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="w-8 h-[2px] bg-brand-accent" />
            <span className="text-brand-primary font-body font-bold uppercase tracking-[0.3em] text-[11px]">
              {servicesSection.badge || "Nuestros Servicios"}
            </span>
          </motion.div>

          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-5xl md:text-6xl font-black text-brand-dark mb-8 leading-[1.1] tracking-tighter max-w-3xl"
          >
            {servicesSection.title} <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-mid italic font-semibold pb-3 pr-3 box-decoration-clone inline-block leading-[1.3]">
              {servicesSection.titleHighlight}
            </span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="font-body text-brand-dark/70 text-xl max-w-2xl font-light leading-relaxed border-l-2 border-brand-accent/30 pl-8"
          >
            {servicesSection.description}
          </motion.p>
        </div>

        {/* GRID DE SERVICIOS: TARJETAS PREMIUM */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {servicesSection.items.map((service, index) => (
            <motion.article
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-[40px] p-10 shadow-[0_20px_50px_rgba(0,0,0,0.03)] hover:shadow-[0_40px_80px_rgba(0,0,0,0.08)] border border-brand-soft transition-all duration-500 group flex flex-col justify-between h-full"
            >
              <div>
                <GreenIconTile className="mb-8">
                  {service.icon ? (
                    <ResolveCmsIcon icon={service.icon} size={TILE_ICON_SIZE} alt={service.title} />
                  ) : (
                    <ShieldCheck size={TILE_ICON_SIZE} />
                  )}
                </GreenIconTile>
                
                <h3 className="font-display text-2xl font-black text-brand-dark mb-4 tracking-tight">
                  {service.title}
                </h3>
                
                <p className="font-body text-brand-dark/60 text-base leading-relaxed font-normal">
                  {service.description}
                </p>
              </div>

              <div className="mt-10 pt-6 border-t border-brand-soft flex items-center justify-between">
                {service.id === "lab" ? (
                  <Link
                    to="/labsyso"
                    className="font-body text-[10px] font-bold uppercase tracking-widest text-brand-primary opacity-80 hover:opacity-100 transition-opacity"
                  >
                    Explorar Laboratorio
                  </Link>
                ) : service.id === "higiene" ? (
                  <Link
                    to="/servicios?id=higiene-industrial"
                    className="font-body text-[10px] font-bold uppercase tracking-widest text-brand-primary opacity-80 hover:opacity-100 transition-opacity"
                  >
                    Instrumentos de Medicion
                  </Link>
                ) : service.id === "riesgo" ? (
                  <button
                    onClick={() => setModalOpen(true)}
                    className="font-body text-[10px] font-bold uppercase tracking-widest text-brand-primary opacity-80 hover:opacity-100 transition-opacity"
                  >
                    Dashboard Estadistico
                  </button>
                ) : (
                  <span className="font-body text-[10px] font-bold uppercase tracking-widest text-brand-primary opacity-40 group-hover:opacity-100 transition-opacity">
                    Explorar Servicio Lab
                  </span>
                )}

                <div className="text-brand-accent transform group-hover:translate-x-2 transition-transform">
                  <ChevronRight size={20} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      <ConstructionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        message="Dashboard Estadístico: disponible próximamente."
      />
    </section>
  );
};

export default Services;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/